import Vue from "vue";
import * as Sentry from "@sentry/vue";
import { isNavigationFailure, NavigationFailureType } from "vue-router";

export const DEFAULT_ERROR_MESSAGE = "We couldn't complete that action. Please try again.";

export const errorState = Vue.observable({
  message: DEFAULT_ERROR_MESSAGE,
  visible: false,
});

const reportedErrors = new WeakSet<object>();

const isObject = (value: unknown): value is object =>
  (typeof value === "object" && value !== null) || typeof value === "function";

export const markErrorReported = (error: unknown) => {
  if (isObject(error)) reportedErrors.add(error);
};

export const reportError = (error: unknown) => {
  if (isObject(error) && reportedErrors.has(error)) return;

  Sentry.captureException(error);
  markErrorReported(error);
};

export const showError = (message = DEFAULT_ERROR_MESSAGE) => {
  errorState.message = message;
  errorState.visible = true;
};

export const handleError = (error: unknown) => {
  reportError(error);
  showError();
};

export const handleNavigationError = (error: unknown) => {
  if (
    isNavigationFailure(error, NavigationFailureType.duplicated) ||
    isNavigationFailure(error, NavigationFailureType.cancelled) ||
    isNavigationFailure(error, NavigationFailureType.aborted)
  ) {
    return;
  }

  handleError(error);
};

export const installGlobalErrorHandling = () => {
  const sentryErrorHandler = Vue.config.errorHandler;

  Vue.config.errorHandler = (error, vm, info) => {
    showError();

    if (sentryErrorHandler) {
      sentryErrorHandler(error, vm, info);
    } else {
      reportError(error);
    }
  };

  window.addEventListener("unhandledrejection", (event) => reportError(event.reason));
};
